import React, { useState, useEffect } from 'react';
import {
  ShoppingCart, FileText, Package, ListChecks, BarChart3, History,
  Truck, ClipboardList, TrendingUp, MapPin, Wrench, Database, Calculator,
  ChevronDown, ChevronRight, Grid3x3, Flame, Radio, Pin, PinOff, Settings,
  Zap, Edit, Move, ScrollText, FolderTree, Link2, Box, Users
} from 'lucide-react';
import { Card } from '../../common/Card';
import { useAuth } from '../../../contexts/AuthContext';
import shortcutService from '../../../services/shortcutService';

interface ManagerDashboardProps {
  onModuleChange: (moduleId: string, event?: React.MouseEvent) => void;
}

// Interface for gruppering/undergruppering struktur
interface DashboardItem {
  id: string;
  name: string;
  icon: any;
  iconName: string; // String name of the icon for serialization
  color: string;
  description: string;
  moduleId?: string; // Hvis det finnes en faktisk modul å navigere til
}

interface DashboardSection {
  id: string;
  name: string;
  items: DashboardItem[];
}

interface DashboardGroup {
  id: string;
  name: string;
  icon: any;
  color: string;
  sections: DashboardSection[];
}

export const ManagerDashboard: React.FC<ManagerDashboardProps> = ({ onModuleChange }) => {
  const { user } = useAuth();
  const [pinnedShortcuts, setPinnedShortcuts] = useState<Set<string>>(new Set());

  // Load pinned shortcuts on mount
  useEffect(() => {
    const shortcuts = shortcutService.getShortcuts();
    setPinnedShortcuts(new Set(shortcuts.map(s => s.id)));
  }, []);

  // Listen for shortcut changes from other components
  useEffect(() => {
    const handleShortcutsChanged = () => {
      const shortcuts = shortcutService.getShortcuts();
      setPinnedShortcuts(new Set(shortcuts.map(s => s.id)));
    };

    window.addEventListener('shortcuts-changed', handleShortcutsChanged);
    return () => window.removeEventListener('shortcuts-changed', handleShortcutsChanged);
  }, []);

  // Access control based on operatorGroupId
  // GroupID 1 (Operatør): Only operator functions
  // GroupID 2 (Lagersjef): All functions
  // GroupID 3 (IPB/Admin): All functions
  const isOperatorOnly = user?.operatorGroupId === '1';
  const canAccessManagerFunctions = user?.operatorGroupId === '2' || user?.operatorGroupId === '3';

  // Definer dashboardstrukturen
  const dashboardGroups: DashboardGroup[] = [
    {
      id: 'plukk-pakk',
      name: 'Plukk & Pakk',
      icon: Package,
      color: 'blue',
      sections: [
        {
          id: 'nye-ordre',
          name: 'Nye Ordre',
          items: [
            {
              id: 'ordre',
              name: 'Ordre',
              icon: ShoppingCart,
              iconName: 'ShoppingCart',
              color: 'blue',
              description: 'Administrer nye ordre',
              moduleId: 'new-orders' // Navigerer til NewOrders
            },
            {
              id: 'ordrelinje',
              name: 'Ordrelinje',
              icon: FileText,
              iconName: 'FileText',
              color: 'blue',
              description: 'Detaljert ordrelinjevisning',
              moduleId: 'order-lines' // Navigerer til OrderLines
            }
          ]
        },
        {
          id: 'ordre-under-arbeid',
          name: 'Plukklister',
          items: [
            {
              id: 'nye-plukklister',
              name: 'Nye plukklister',
              icon: ListChecks,
              iconName: 'ListChecks',
              color: 'green',
              description: 'Se nye plukklister',
              moduleId: 'new-picking-lists'
            },
            {
              id: 'plukklister-arbeid',
              name: 'Plukklister under arbeid',
              icon: Package,
              iconName: 'Package',
              color: 'yellow',
              description: 'Aktive plukklister',
              moduleId: 'working-picking-lists'
            }
          ]
        },
        {
          id: 'historikk-plukk',
          name: 'Historikk',
          items: [
            {
              id: 'avsluttede-plukklister',
              name: 'Avsluttede plukklister',
              icon: ListChecks,
              iconName: 'ListChecks',
              color: 'gray',
              description: 'Se avsluttede plukklister',
              moduleId: 'completed-picking-lists'
            },
            {
              id: 'ordrehistorikk',
              name: 'Ordrehistorikk',
              icon: History,
              iconName: 'History',
              color: 'gray',
              description: 'Historiske ordre',
              moduleId: 'order-history'
            },
            {
              id: 'statistikk',
              name: 'Statistikk',
              icon: BarChart3,
              iconName: 'BarChart3',
              color: 'purple',
              description: 'Statistikk og rapporter',
              moduleId: 'statistics'
            }
          ]
        }
      ]
    },
    {
      id: 'varemottak',
      name: 'Varemottak',
      icon: Truck,
      color: 'purple',
      sections: [
        {
          id: 'bestilling',
          name: 'Bestilling',
          items: [
            {
              id: 'bestilling',
              name: 'Administrer bestillinger',
              icon: FileText,
              iconName: 'FileText',
              color: 'purple',
              description: 'Administrer bestillinger',
              moduleId: 'working-orders'
            },
            {
              id: 'bestillingslinje',
              name: 'Bestillingslinje',
              icon: FileText,
              iconName: 'FileText',
              color: 'purple',
              description: 'Detaljert bestillingslinjevisning',
              moduleId: 'purchase-order-lines'
            },
            {
              id: 'historikk-bestilling',
              name: 'Historikk',
              icon: History,
              iconName: 'History',
              color: 'gray',
              description: 'Bestillingshistorikk',
              moduleId: 'purchase-order-history'
            }
          ]
        },
        {
          id: 'varemottak-section',
          name: 'Varemottak',
          items: [
            {
              id: 'varemottak',
              name: 'Varemottak',
              icon: Truck,
              iconName: 'Truck',
              color: 'purple',
              description: 'Motta varer',
              moduleId: 'receipt'
            },
            {
              id: 'varemottakslinjer',
              name: 'Varemottakslinjer',
              icon: ClipboardList,
              iconName: 'ClipboardList',
              color: 'purple',
              description: 'Varemottakslinjer',
              moduleId: 'receipt'
            }
          ]
        }
      ]
    },
    {
      id: 'lager',
      name: 'Lager',
      icon: Database,
      color: 'teal',
      sections: [
        {
          id: 'transaksjoner',
          name: 'Transaksjoner',
          items: [
            {
              id: 'vare-lokasjonssaldo',
              name: 'Vare og lokasjonssaldo',
              icon: TrendingUp,
              iconName: 'TrendingUp',
              color: 'teal',
              description: 'Saldo oversikt',
              moduleId: 'item-location-balance'
            },
            {
              id: 'transaksjonshistorikk',
              name: 'Transaksjonshistorikk',
              icon: History,
              iconName: 'History',
              color: 'teal',
              description: 'Historiske transaksjoner',
              moduleId: 'transaction-history'
            },
            {
              id: 'reservasjoner',
              name: 'Reservasjoner',
              icon: ClipboardList,
              iconName: 'ClipboardList',
              color: 'teal',
              description: 'Vareserverasjoner',
              moduleId: 'reservations'
            },
            {
              id: 'innkjopspartier',
              name: 'Innkjøpspartier',
              icon: Package,
              iconName: 'Package',
              color: 'teal',
              description: 'Innkjøpspartier',
              moduleId: 'receipt'
            }
          ]
        },
        {
          id: 'vareplassering',
          name: 'Vareplassering',
          items: [
            {
              id: 'grunnlag',
              name: 'Grunnlag',
              icon: Database,
              iconName: 'Database',
              color: 'green',
              description: 'Grunnlag for vareplassering',
              moduleId: 'bin-placement-grunnlag'
            },
            {
              id: 'plassering-ordre',
              name: 'Ordre',
              icon: ShoppingCart,
              iconName: 'ShoppingCart',
              color: 'green',
              description: 'Plasseringsordre',
              moduleId: 'put-away'
            },
            {
              id: 'plassering-ordrelinjer',
              name: 'Ordrelinjer',
              icon: FileText,
              iconName: 'FileText',
              color: 'green',
              description: 'Plasseringsordrelinjer',
              moduleId: 'put-away'
            }
          ]
        },
        {
          id: 'verktoy',
          name: 'Verktøy',
          items: [
            {
              id: 'oprett-utlegg',
              name: 'Oprett utlegg',
              icon: Wrench,
              iconName: 'Wrench',
              color: 'orange',
              description: 'Oprett utlegg',
              moduleId: 'movement'
            },
            {
              id: 'oprett-pafylling',
              name: 'Opprett påfylling',
              icon: Wrench,
              iconName: 'Wrench',
              color: 'orange',
              description: 'Opprett påfylling',
              moduleId: 'movement'
            }
          ]
        },
        {
          id: 'grunndata',
          name: 'Grunndata',
          items: [
            {
              id: 'vedlikehold-lager',
              name: 'Vedlikehold Lager',
              icon: Wrench,
              iconName: 'Wrench',
              color: 'indigo',
              description: 'Vedlikehold av lager',
              moduleId: 'warehouse-maintenance'
            },
            {
              id: 'reolbygger',
              name: 'Reolbygger',
              icon: Grid3x3,
              iconName: 'Grid3x3',
              color: 'indigo',
              description: 'Administrer reoler',
              moduleId: 'rack-builder'
            },
            {
              id: 'location-heatmap',
              name: 'Lokasjon HeatMap',
              icon: Flame,
              iconName: 'Flame',
              color: 'indigo',
              description: 'Visualiser lokasjonsaktivitet',
              moduleId: 'location-heatmap'
            },
            {
              id: 'rack-builder-live',
              name: 'Live Aktivitet',
              icon: Radio,
              iconName: 'Radio',
              color: 'indigo',
              description: 'Sanntidsvisning av lageraktivitet',
              moduleId: 'rack-builder-live'
            },
            {
              id: 'warehouse-layout-3d',
              name: '3D Lagervisning',
              icon: Box,
              iconName: 'Box',
              color: 'indigo',
              description: 'Visuell 3D-representasjon av lageret',
              moduleId: 'warehouse-layout-3d'
            },
          ]
        },
        {
          id: 'lagertelling',
          name: 'Lagertelling',
          items: [
            {
              id: 'lagertelling',
              name: 'Lagertelling',
              icon: Calculator,
              iconName: 'Calculator',
              color: 'red',
              description: 'Utfør lagertelling',
              moduleId: 'inventory-counting-dashboard'
            },
            {
              id: 'lagertelling-linjer',
              name: 'Lagertelling linjer',
              icon: FileText,
              iconName: 'FileText',
              color: 'red',
              description: 'Lagertellinglinjer',
              moduleId: 'inventory'
            },
            {
              id: 'lagertelling-summert',
              name: 'Lagertelling summert',
              icon: BarChart3,
              iconName: 'BarChart3',
              color: 'red',
              description: 'Summert lagertelling',
              moduleId: 'inventory'
            },
            {
              id: 'historikk-telling',
              name: 'Historikk',
              icon: History,
              iconName: 'History',
              color: 'gray',
              description: 'Lagertellinghistorikk',
              moduleId: 'history'
            }
          ]
        }
      ]
    },
    {
      id: 'system',
      name: 'System',
      icon: Settings,
      color: 'gray',
      sections: [
        {
          id: 'konfigurajon',
          name: 'Konfigurasjon',
          items: [
            {
              id: 'rapportkonfigurasjon',
              name: 'Rapportkonfigurasjon',
              icon: FileText,
              iconName: 'FileText',
              color: 'gray',
              description: 'Administrer rapporter og utskrifter',
              moduleId: 'report-configuration'
            },
            {
              id: 'lagersted',
              name: 'Lagersted',
              icon: Database,
              iconName: 'Database',
              color: 'gray',
              description: 'Administrer lagersted konfigurasjoner',
              moduleId: 'warehouse-management'
            },
            {
              id: 'regler',
              name: 'Regler',
              icon: ScrollText,
              iconName: 'ScrollText',
              color: 'gray',
              description: 'Vedlikehold systemregler og SQL-uttrykk',
              moduleId: 'system-rules'
            },
            {
              id: 'regelgrupper',
              name: 'Regelgrupper',
              icon: FolderTree,
              iconName: 'FolderTree',
              color: 'gray',
              description: 'Administrer grupper for systemregler',
              moduleId: 'system-rule-groups'
            },
            {
              id: 'regelsammensetning',
              name: 'Regelsammensetning',
              icon: Link2,
              iconName: 'Link2',
              color: 'gray',
              description: 'Koble regler til regelgrupper med drag-and-drop',
              moduleId: 'rule-composition'
            },
            {
              id: 'sql-syntax',
              name: 'SQL Syntaks',
              icon: Database,
              iconName: 'Database',
              color: 'gray',
              description: 'Administrer SQL filter, gruppering og sortering',
              moduleId: 'sql-syntax-admin'
            },
            {
              id: 'profile-admin',
              name: 'Profiler',
              icon: Users,
              iconName: 'Users',
              color: 'gray',
              description: 'Administrer brukerprofiler og tilganger',
              moduleId: 'profile-admin'
            },
            {
              id: 'system-settings',
              name: 'Systeminnstillinger',
              icon: Settings,
              iconName: 'Settings',
              color: 'gray',
              description: 'Globale systemkonfigurasjoner',
              moduleId: 'system-settings'
            }
          ]
        }
      ]
    }
  ];

  // Operator modules (from Dashboard.tsx)
  const operatorModules: DashboardItem[] = [
    {
      id: 'receipt-operator',
      name: 'Varemottak',
      icon: Truck,
      iconName: 'Truck',
      color: 'purple',
      description: 'Motta leveranser',
      moduleId: 'receipt'
    },
    {
      id: 'put-away-operator',
      name: 'Plassering',
      icon: MapPin,
      iconName: 'MapPin',
      color: 'green',
      description: 'Plasser varer',
      moduleId: 'put-away'
    },
    {
      id: 'modern-picking-operator',
      name: 'Plukk',
      icon: Zap,
      iconName: 'Zap',
      color: 'yellow',
      description: 'Effektiv plukking',
      moduleId: 'modern-picking'
    },
    {
      id: 'packing-operator',
      name: 'Pakking',
      icon: Package,
      iconName: 'Package',
      color: 'orange',
      description: 'Pakk og klargjør forsendelser',
      moduleId: 'packing'
    },
    {
      id: 'inventory-operator',
      name: 'Lagertelling',
      icon: ClipboardList,
      iconName: 'ClipboardList',
      color: 'indigo',
      description: 'Tell lagerbeholdning',
      moduleId: 'inventory'
    },
    {
      id: 'correction-operator',
      name: 'Korreksjon',
      icon: Edit,
      iconName: 'Edit',
      color: 'red',
      description: 'Korriger feil',
      moduleId: 'correction'
    },
    {
      id: 'movement-operator',
      name: 'Lager-/Lokasjonsflytt',
      icon: Move,
      iconName: 'Move',
      color: 'cyan',
      description: 'Flytt varer',
      moduleId: 'movement'
    },
    {
      id: 'rack-builder-live-operator',
      name: 'Live Aktivitet',
      icon: Radio,
      iconName: 'Radio',
      color: 'blue',
      description: 'Sanntidsvisning av lageraktivitet',
      moduleId: 'rack-builder-live'
    },
    {
      id: 'item-admin-operator',
      name: 'Vareadministrasjon',
      icon: FileText,
      iconName: 'FileText',
      color: 'teal',
      description: 'Administrer varer',
      moduleId: 'item-admin'
    },
    {
      id: 'history-operator',
      name: 'Historikk',
      icon: History,
      iconName: 'History',
      color: 'gray',
      description: 'Se transaksjonshistorikk',
      moduleId: 'history'
    }
  ];

  const handleItemClick = (item: DashboardItem, event?: React.MouseEvent) => {
    if (item.moduleId) {
      onModuleChange(item.moduleId, event);
    }
    // Hvis ingen moduleId, gjør ingenting (dummy link)
  };

  const handleTogglePin = (item: DashboardItem, groupId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    if (!item.moduleId) return; // Can't pin items without moduleId

    const shortcutGroup = mapGroupIdToShortcutGroup(groupId);

    shortcutService.toggleShortcut({
      id: item.id,
      title: item.name,
      path: item.moduleId,
      icon: item.iconName,
      group: shortcutGroup
    });
  };

  const mapGroupIdToShortcutGroup = (groupId: string): any => {
    const groupMap: Record<string, string> = {
      'plukk-pakk': 'plukk-pakk',
      'varemottak': 'varemottak',
      'lager': 'lager',
      'historikk': 'historikk',
      'vareplassering': 'vareplassering',
      'transaksjoner': 'transaksjoner',
      'verktoy': 'verktoy',
      'grunndata': 'grunndata',
      'lagerstilling': 'lagerstilling'
    };
    return groupMap[groupId] || 'lager';
  };

  const getColorClasses = (color: string) => {
    const colorMap: { [key: string]: string } = {
      blue: 'bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-900',
      purple: 'bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-900',
      green: 'bg-green-50 hover:bg-green-100 border-green-200 text-green-900',
      yellow: 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200 text-yellow-900',
      teal: 'bg-teal-50 hover:bg-teal-100 border-teal-200 text-teal-900',
      cyan: 'bg-cyan-50 hover:bg-cyan-100 border-cyan-200 text-cyan-900',
      gray: 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-900',
      red: 'bg-red-50 hover:bg-red-100 border-red-200 text-red-900',
      orange: 'bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-900',
      indigo: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200 text-indigo-900'
    };
    return colorMap[color] || 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-900';
  };

  const getIconColorClasses = (color: string) => {
    const iconColorMap: { [key: string]: string } = {
      blue: 'text-blue-600',
      purple: 'text-purple-600',
      green: 'text-green-600',
      yellow: 'text-yellow-600',
      teal: 'text-teal-600',
      cyan: 'text-cyan-600',
      gray: 'text-gray-600',
      red: 'text-red-600',
      orange: 'text-orange-600',
      indigo: 'text-indigo-600'
    };
    return iconColorMap[color] || 'text-gray-600';
  };

  const getGroupColorClasses = (color: string) => {
    const colorMap: { [key: string]: string } = {
      blue: 'bg-blue-100 border-blue-300 text-blue-900',
      purple: 'bg-purple-100 border-purple-300 text-purple-900',
      teal: 'bg-teal-100 border-teal-300 text-teal-900'
    };
    return colorMap[color] || 'bg-gray-100 border-gray-300 text-gray-900';
  };

  return (
    <div className="relative min-h-screen bg-gray-50">
      {/* Main content area - med padding bottom for fast statusbar */}
      <div className="pb-20">
        <div className="p-4 w-full">
          {/* Header område */}
          <div className="mb-6">
            <div className="bg-gradient-to-r from-teal-500 to-blue-600 text-white p-6 rounded-lg shadow-lg">
              <h2 className="text-2xl font-bold mb-2">Velkommen, {user?.operatorName}!</h2>
              <div className="text-sm opacity-90">
                <p>
                  {isOperatorOnly
                    ? 'Operatør Dashboard • Utfør dine daglige oppgaver'
                    : 'Lagersjef Dashboard • Administrer alle lageroperasjoner'}
                </p>
              </div>
            </div>
          </div>

          {/* Manager functions - Only visible for Lagersjef and Admin */}
          {canAccessManagerFunctions && (
            <div className="space-y-6 mb-6">
              {/* ROW 1: Plukk & Pakk og Varemottak side ved side - samme stil som Lager */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {dashboardGroups.filter(g => g.id === 'plukk-pakk' || g.id === 'varemottak').map((group) => {
                  const GroupIcon = group.icon;
                  return (
                    <div key={group.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                      <div className={`p-4 rounded-t-lg ${getGroupColorClasses(group.color)} border-b-2`}>
                        <div className="flex items-center space-x-3">
                          <GroupIcon size={24} className={getIconColorClasses(group.color)} />
                          <h3 className="font-bold text-lg">{group.name}</h3>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-2 xl:grid-cols-3 gap-6">
                          {group.sections.map((section) => (
                            <div key={section.id}>
                              <h4 className="font-semibold text-sm text-gray-600 mb-3 uppercase tracking-wide border-b border-gray-100 pb-2">
                                {section.name}
                              </h4>
                              <div className="space-y-2">
                                {section.items.map((item) => {
                                  const ItemIcon = item.icon;
                                  const isPinned = pinnedShortcuts.has(item.id);
                                  return (
                                    <div key={item.id} className="relative group">
                                      <button
                                        onClick={(e) => handleItemClick(item, e)}
                                        className={`${getColorClasses(item.color)} w-full p-3 rounded-lg border-2 transition-all duration-200 transform hover:scale-105 hover:shadow-lg text-left flex items-center gap-3 ${
                                          !item.moduleId ? 'cursor-default opacity-75' : 'cursor-pointer'
                                        }`}
                                        disabled={!item.moduleId}
                                      >
                                        <ItemIcon size={20} className={`${getIconColorClasses(item.color)} flex-shrink-0`} />
                                        <div className="min-w-0">
                                          <h5 className="font-semibold text-xs leading-tight truncate">{item.name}</h5>
                                          <p className="text-[10px] opacity-70 truncate">{item.description}</p>
                                        </div>
                                      </button>
                                      {item.moduleId && (
                                        <button
                                          onClick={(e) => handleTogglePin(item, group.id, e)}
                                          className={`absolute top-1.5 right-1.5 p-1 rounded transition-all duration-200 ${
                                            isPinned ? 'bg-blue-500 text-white opacity-100' : 'bg-white/80 text-gray-600 opacity-0 group-hover:opacity-100'
                                          } hover:scale-110 shadow-sm`}
                                          title={isPinned ? 'Fjern fra snarveier' : 'Legg til snarvei'}
                                        >
                                          {isPinned ? <Pin size={12} /> : <PinOff size={12} />}
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ROW 2: Lager - full bredde */}
              {dashboardGroups.filter(g => g.id === 'lager').map((group) => {
                const GroupIcon = group.icon;
                return (
                  <div key={group.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className={`p-4 rounded-t-lg ${getGroupColorClasses(group.color)} border-b-2`}>
                      <div className="flex items-center space-x-3">
                        <GroupIcon size={24} className={getIconColorClasses(group.color)} />
                        <h3 className="font-bold text-lg">{group.name}</h3>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                        {group.sections.map((section) => (
                          <div key={section.id}>
                            <h4 className="font-semibold text-sm text-gray-600 mb-3 uppercase tracking-wide border-b border-gray-100 pb-2">
                              {section.name}
                            </h4>
                            <div className="space-y-2">
                              {section.items.map((item) => {
                                const ItemIcon = item.icon;
                                const isPinned = pinnedShortcuts.has(item.id);
                                return (
                                  <div key={item.id} className="relative group">
                                    <button
                                      onClick={(e) => handleItemClick(item, e)}
                                      className={`${getColorClasses(item.color)} w-full p-3 rounded-lg border-2 transition-all duration-200 transform hover:scale-105 hover:shadow-lg text-left flex items-center gap-3 ${
                                        !item.moduleId ? 'cursor-default opacity-75' : 'cursor-pointer'
                                      }`}
                                      disabled={!item.moduleId}
                                    >
                                      <ItemIcon size={20} className={`${getIconColorClasses(item.color)} flex-shrink-0`} />
                                      <div className="min-w-0">
                                        <h5 className="font-semibold text-xs leading-tight truncate">{item.name}</h5>
                                        <p className="text-[10px] opacity-70 truncate">{item.description}</p>
                                      </div>
                                    </button>
                                    {item.moduleId && (
                                      <button
                                        onClick={(e) => handleTogglePin(item, group.id, e)}
                                        className={`absolute top-1.5 right-1.5 p-1 rounded transition-all duration-200 ${
                                          isPinned ? 'bg-blue-500 text-white opacity-100' : 'bg-white/80 text-gray-600 opacity-0 group-hover:opacity-100'
                                        } hover:scale-110 shadow-sm`}
                                        title={isPinned ? 'Fjern fra snarveier' : 'Legg til snarvei'}
                                      >
                                        {isPinned ? <Pin size={12} /> : <PinOff size={12} />}
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* ROW 3: System - full bredde */}
              {dashboardGroups.filter(g => g.id === 'system').map((group) => {
                const GroupIcon = group.icon;
                return (
                  <div key={group.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className={`p-4 rounded-t-lg bg-gray-100 border-gray-300 border-b-2`}>
                      <div className="flex items-center space-x-3">
                        <GroupIcon size={24} className="text-gray-600" />
                        <h3 className="font-bold text-lg text-gray-900">{group.name}</h3>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
                        {group.sections.flatMap((section) =>
                          section.items.map((item) => {
                            const ItemIcon = item.icon;
                            const isPinned = pinnedShortcuts.has(item.id);
                            return (
                              <div key={item.id} className="relative group">
                                <button
                                  onClick={(e) => handleItemClick(item, e)}
                                  className={`${getColorClasses(item.color)} w-full p-3 rounded-lg border-2 transition-all duration-200 transform hover:scale-105 hover:shadow-lg text-left flex flex-col justify-between min-h-[90px] ${
                                    !item.moduleId ? 'cursor-default opacity-75' : 'cursor-pointer'
                                  }`}
                                  disabled={!item.moduleId}
                                >
                                  <div>
                                    <ItemIcon size={22} className={`${getIconColorClasses(item.color)} mb-2`} />
                                    <h5 className="font-semibold text-xs leading-tight">{item.name}</h5>
                                  </div>
                                  <p className="text-[10px] opacity-70 mt-1 line-clamp-1">{item.description}</p>
                                </button>
                                {item.moduleId && (
                                  <button
                                    onClick={(e) => handleTogglePin(item, group.id, e)}
                                    className={`absolute top-1.5 right-1.5 p-1 rounded transition-all duration-200 ${
                                      isPinned ? 'bg-blue-500 text-white opacity-100' : 'bg-white/80 text-gray-600 opacity-0 group-hover:opacity-100'
                                    } hover:scale-110 shadow-sm`}
                                    title={isPinned ? 'Fjern fra snarveier' : 'Legg til snarvei'}
                                  >
                                    {isPinned ? <Pin size={12} /> : <PinOff size={12} />}
                                  </button>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ROW 4: Operator functions - full bredde */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Section header */}
            <div className="p-4 rounded-t-lg bg-gradient-to-r from-purple-100 to-blue-100 border-b-2 border-purple-300">
              <div className="flex items-center space-x-3">
                <Zap size={24} className="text-purple-600" />
                <h3 className="font-bold text-lg text-purple-900">
                  {isOperatorOnly ? 'Mine Oppgaver' : 'Operatør-funksjoner'}
                </h3>
              </div>
            </div>

            {/* Operator modules grid - horisontalt i én rad på store skjermer */}
            <div className="p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-10 gap-3">
                {operatorModules.map((item) => {
                  const ItemIcon = item.icon;
                  const isPinned = pinnedShortcuts.has(item.id);

                  return (
                    <div key={item.id} className="relative group">
                      <button
                        onClick={(e) => handleItemClick(item, e)}
                        className={`${getColorClasses(item.color)} w-full p-3 rounded-lg border-2 transition-all duration-200 transform hover:scale-105 hover:shadow-lg text-left flex flex-col items-center justify-center min-h-[90px] cursor-pointer`}
                      >
                        <ItemIcon size={28} className={`${getIconColorClasses(item.color)} mb-2`} />
                        <h5 className="font-semibold text-[11px] leading-tight text-center">{item.name}</h5>
                      </button>

                      {/* Pin button */}
                      <button
                        onClick={(e) => handleTogglePin(item, 'operator', e)}
                        className={`absolute top-1 right-1 p-1 rounded transition-all duration-200 ${
                          isPinned ? 'bg-blue-500 text-white opacity-100' : 'bg-white/80 text-gray-600 opacity-0 group-hover:opacity-100'
                        } hover:scale-110 shadow-sm`}
                        title={isPinned ? 'Fjern fra snarveier' : 'Legg til snarvei'}
                      >
                        {isPinned ? <Pin size={12} /> : <PinOff size={12} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fast statusbar i bunnen */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-10">
        <div className="bg-gray-100 px-4 py-3">
          <div className="flex items-center justify-between text-sm text-gray-600 w-full">
            <div className="flex items-center space-x-4">
              <span>System: <span className="text-green-600 font-medium">Online</span></span>
              <span>•</span>
              <span>Lager: Hovedlager</span>
              <span>•</span>
              <span>Rolle: {isOperatorOnly ? 'Operatør' : 'Lagersjef'}</span>
            </div>
            <div className="text-xs">
              Sist oppdatert: {new Date().toLocaleTimeString('no-NO')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
